/**
 * Central config + content for Grade 10 Programming & AI curriculum SEO page.
 * Sources: official Arabic/English books + Term 1/Term 2 distribution for 2025/2026.
 * Do not invent ministry claims or treat 2025/2026 materials as 2026/2027 official files.
 */

export const STATUS_LABELS = {
    'term-1': 'تم تدريسه في الترم الأول',
    'term-2': 'تم تدريسه في الترم الثاني',
    'not-in-distribution': 'موجود في الكتاب وغير مدرج في التوزيع الرسمي 2025/2026',
};

export const TYPE_LABELS = {
    theory: 'نظري',
    practical: 'عملي',
    project: 'مشروع',
};

export const FILTER_OPTIONS = [
    { id: 'all', label: 'المنهج كاملًا', query: null },
    { id: 'term-1', label: 'الترم الأول', query: 'term-1', field: 'status' },
    { id: 'term-2', label: 'الترم الثاني', query: 'term-2', field: 'status' },
    { id: 'not-in-distribution', label: 'غير مدرج في التوزيع', query: 'not-scheduled', field: 'status' },
    { id: 'theory', label: 'الجانب النظري', query: 'theory', field: 'type' },
    { id: 'practical', label: 'الجانب العملي', query: 'practical', field: 'type' },
];

export const grade10CurriculumConfig = {
    officialAcademicYear: '2025/2026',
    currentAcademicYear: '2026/2027',
    lastReviewedDate: '2026-08-07',
    canonicalUrl: 'https://codingwithghonaim.com/grade-10-programming-ai-curriculum',
    routePath: '/grade-10-programming-ai-curriculum',
    routeName: 'grade-10-programming-ai-curriculum',

    arabicBookUrl: '/downloads/grade-10-programming-ai-arabic-book-2025-2026.pdf',
    englishBookUrl: '/downloads/grade-10-programming-ai-english-book-2025-2026.pdf',
    arabicBookSize: '7.1 MB', // approx from 7460363 bytes
    englishBookSize: '7.1 MB',

    grade10CourseUrl: '/courses/10',
    grade10CourseRoute: { name: 'course', params: { id: 10 } },
    grade10TermOneUrl: '/courses/10',
    grade10TermOneRoute: { name: 'course', params: { id: 10 } },

    grade11CurriculumUrl: '/grade-11-programming-ai-curriculum',
    grade11CurriculumRoute: { name: 'grade-11-programming-ai-curriculum' },

    aboutGhonaimUrl: '/about',
    aboutGhonaimRoute: { name: 'about' },

    registrationUrl: '/register',
    registrationRoute: {
        name: 'register',
        query: { grade_slug: 'grade-10', grade_name: 'أولى ثانوي' },
    },

    paymentInfoUrl: '/courses/10',
    paymentInfoRoute: { name: 'course', params: { id: 10 } },

    homeUrl: '/',
    homeRoute: { name: 'welcome' },

    coursesUrl: '/courses',
    coursesRoute: { name: 'courses' },

    grade10LandingUrl: '/grade-10',
    grade10LandingRoute: { name: 'grade-10' },

    // Branded 1200×630 Open Graph image for social crawlers
    // /images/seo/grade-10-curriculum/grade-10-programming-ai-curriculum-og.jpg
    socialImageUrl: 'https://codingwithghonaim.com/images/seo/grade-10-curriculum/grade-10-programming-ai-curriculum-og.jpg',

    unitCount: 13,
};

export const heroChips = [
    'الكتاب الرسمي',
    'عربي ولغات',
    'الترم الأول والثاني',
    'نظري وعملي',
    'تحميل PDF',
    'آخر توزيع رسمي متاح',
];

export const heroFlow = [
    { label: 'Information', icon: 'Info' },
    { label: 'Security', icon: 'Shield' },
    { label: 'Digital Systems', icon: 'Binary' },
    { label: 'Networks', icon: 'Network' },
    { label: 'Databases', icon: 'Database' },
    { label: 'Programming', icon: 'Code2' },
    { label: 'AI Projects', icon: 'Brain' },
];

export const summaryGrid = [
    { label: '13 وحدة', icon: 'BookOpen' },
    { label: 'كتاب عربي وEnglish', icon: 'Languages' },
    { label: 'محتوى نظري', icon: 'FileText' },
    { label: 'Qureo JavaScript', icon: 'Code2' },
    { label: 'مشروعات Web', icon: 'Globe' },
    { label: 'Generative AI', icon: 'Brain' },
    { label: 'تدريبات وأسئلة', icon: 'ClipboardList' },
    { label: 'تقييمات ومراجعات', icon: 'CheckCircle2' },
];

export const statusGuide = [
    {
        id: 'term-1',
        title: STATUS_LABELS['term-1'],
        meaning: 'درس ظهر في التوزيع الرسمي للترم الأول.',
        color: 'green',
    },
    {
        id: 'term-2',
        title: STATUS_LABELS['term-2'],
        meaning: 'درس ظهر في التوزيع الرسمي للترم الثاني.',
        color: 'blue',
    },
    {
        id: 'not-in-distribution',
        title: 'موجود في الكتاب وغير مدرج في التوزيع الرسمي',
        meaning:
            'الدرس موجود داخل الكتاب الكامل، لكنه لم يظهر ضمن الدروس المطلوب تدريسها في التوزيع الرسمي المتاح للعام 2025/2026.',
        color: 'orange',
        note: 'عدم إدراج درس في التوزيع لا يعني حذفه من الكتاب نفسه.',
    },
];

export const benefits = [
    'فهم الفرق بين البيانات والمعلومات والمعرفة.',
    'استخدام الإنترنت ووسائل التواصل بصورة أكثر أمانًا.',
    'معرفة أساسيات أمن المعلومات والتصيد والبرامج الضارة.',
    'فهم Binary وHexadecimal وطريقة تمثيل البيانات.',
    'التعرف على مكونات الكمبيوتر والبرمجيات.',
    'فهم الشبكات وعناوين IP والبروتوكولات.',
    'معرفة أساسيات قواعد البيانات.',
    'تعلم التفكير بالخوارزميات.',
    'تطبيق Variables وConditions وLoops وFunctions.',
    'بناء صفحات باستخدام HTML وCSS وJavaScript.',
    'استخدام Generative AI بصورة واعية.',
    'تطوير تطبيق اختبارات ولعبة بسيطة.',
    'الاستعداد بشكل أقوى لمنهج تانية ثانوي.',
];

export const studySteps = [
    'راجع عنوان الدرس والمصطلحات الرئيسية.',
    'افهم الفكرة قبل حفظ التعريف.',
    'اربط المفهوم بمثال من الموبايل أو الإنترنت.',
    'حل Warm Up وTry وExercise من الكتاب.',
    'اكتب الكود بنفسك على Qureo.',
    'لا تكتفِ بمشاهدة الحل.',
    'سجّل الأخطاء التي تتكرر معك.',
    'راجع الدروس المقررة فقط قبل الاختبار.',
    'استخدم قائمة “غير مدرج في التوزيع” حتى لا تتشتت.',
    'راجع من نسخة الكتاب المناسبة لنظام مدرستك.',
    'اختبر نفسك بعد كل وحدة.',
    'ارجع للكورس عند وجود نقطة غير واضحة.',
];

export const courseFeatures = [
    'شرح عربي ولغات.',
    'تغطية الجزء النظري والعملي.',
    'تنظيم المحتوى حسب التوزيع.',
    'شرح Qureo JavaScript.',
    'تدريبات بعد كل درس.',
    'واجبات وQuizzes.',
    'حل أسئلة الكتاب.',
    'مراجعات على الوحدات.',
    'مراجعات قبل الاختبارات.',
    'تطبيقات ومشروعات.',
    'متابعة مستوى الطالب.',
    'ربط منهج أولى ثانوي بما سيحتاجه في تانية ثانوي.',
];

export const theoryColumns = [
    {
        id: 'theory-book',
        title: 'الكتاب النظري',
        icon: 'BookOpen',
        items: [
            'Information',
            'Digital Ethics',
            'Security',
            'Binary and Hexadecimal',
            'Networks',
            'Databases',
            'Algorithms',
            'AI concepts',
        ],
    },
    {
        id: 'qureo',
        title: 'Qureo JavaScript',
        icon: 'Code2',
        items: [
            'Variables',
            'Conditions',
            'Loops',
            'Functions',
            'Arrays',
            'Logical Operators',
            'Interactive Operations',
            'Comprehensive Checks',
        ],
    },
    {
        id: 'web-ai-projects',
        title: 'مشروعات الويب والذكاء الاصطناعي',
        icon: 'Brain',
        items: [
            'HTML',
            'CSS',
            'JavaScript',
            'Static Pages',
            'Dynamic Pages',
            'Quiz App',
            'Block Breaker',
            'Generative AI',
        ],
    },
];

export const downloadBooks = [
    {
        language: 'ar',
        title: 'كتاب البرمجة والذكاء الاصطناعي — عربي',
        url: grade10CurriculumConfig.arabicBookUrl,
        academicYear: grade10CurriculumConfig.officialAcademicYear,
        format: 'PDF',
        fileSize: grade10CurriculumConfig.arabicBookSize,
        details: [
            'الصف الأول الثانوي',
            `العام الدراسي ${grade10CurriculumConfig.officialAcademicYear}`,
            'PDF',
            '13 وحدة',
            'شرح ومراجعات وتدريبات',
            'أكثر من 200 صفحة',
        ],
        downloadLabel: 'تحميل الكتاب العربي PDF',
        openLabel: 'فتح الكتاب في صفحة جديدة',
        trackDownload: 'grade10_arabic_book_download',
        trackOpen: 'grade10_arabic_book_open',
    },
    {
        language: 'en',
        title: 'Programming and Artificial Intelligence — English Book',
        url: grade10CurriculumConfig.englishBookUrl,
        academicYear: grade10CurriculumConfig.officialAcademicYear,
        format: 'PDF',
        fileSize: grade10CurriculumConfig.englishBookSize,
        details: [
            'First Year Secondary',
            `Academic Year ${grade10CurriculumConfig.officialAcademicYear}`,
            'PDF',
            '13 Chapters',
            'Explanations, exercises and projects',
            'English terminology',
        ],
        downloadLabel: 'Download English Book PDF',
        openLabel: 'Open Book',
        trackDownload: 'grade10_english_book_download',
        trackOpen: 'grade10_english_book_open',
    },
];

/**
 * Official curriculum distribution maps (Coding With Ghonaim).
 * Filenames are SEO keyword slugs; dimensions match exported PNGs.
 */
export const curriculumDistributionMaps = [
    {
        id: 'term-1-curriculum-map',
        term: 1,
        label: 'خريطة توزيع منهج البرمجة أولى ثانوي — الترم الأول 2025/2026',
        shortLabel: 'توزيع الترم الأول',
        title: 'خريطة منهج البرمجة والذكاء الاصطناعي أولى ثانوي الترم الأول 2025/2026 | Coding With Ghonaim',
        alt:
            'خريطة توزيع منهج البرمجة والذكاء الاصطناعي أولى ثانوي الترم الأول 2025/2026 أسبوع بأسبوع — معلومات وأمن معلومات وBinary وQureo JavaScript — Coding With Ghonaim',
        caption:
            'توزيع الترم الأول لمنهج البرمجة والذكاء الاصطناعي أولى ثانوي: 9 أسابيع + نهاية الترم (الكتاب النظري، المنصة، وQureo JavaScript).',
        type: 'curriculum-map',
        src: '/images/seo/grade-10-curriculum/grade-10-programming-ai-curriculum-term-1-map-2025-2026.png',
        width: 576,
        height: 1024,
        pending: false,
        keywords: [
            'توزيع الترم الأول أولى ثانوي',
            'منهج البرمجة أولى ثانوي',
            'خريطة المنهج',
            'Qureo JavaScript',
            'Coding With Ghonaim',
        ],
    },
    {
        id: 'term-2-curriculum-map',
        term: 2,
        label: 'خريطة توزيع منهج البرمجة أولى ثانوي — الترم الثاني 2025/2026',
        shortLabel: 'توزيع الترم الثاني',
        title: 'خريطة منهج البرمجة والذكاء الاصطناعي أولى ثانوي الترم الثاني 2025/2026 | Coding With Ghonaim',
        alt:
            'خريطة توزيع منهج البرمجة والذكاء الاصطناعي أولى ثانوي الترم الثاني 2025/2026 أسبوع بأسبوع — شبكات وقواعد بيانات وHTML وCSS وJavaScript ومشروعات Generative AI — Coding With Ghonaim',
        caption:
            'توزيع الترم الثاني لمنهج البرمجة والذكاء الاصطناعي أولى ثانوي: 12 أسبوعًا + نهاية الترم (شبكات، قواعد بيانات، ويب، وAI Projects).',
        type: 'curriculum-map',
        src: '/images/seo/grade-10-curriculum/grade-10-programming-ai-curriculum-term-2-map-2025-2026.png',
        width: 576,
        height: 1024,
        pending: false,
        keywords: [
            'توزيع الترم الثاني أولى ثانوي',
            'منهج البرمجة أولى ثانوي',
            'HTML CSS JavaScript',
            'Generative AI',
            'Coding With Ghonaim',
        ],
    },
];

/**
 * Source gallery: curriculum maps + pending book covers.
 */
export const sourceDocuments = [
    ...curriculumDistributionMaps,
    {
        id: 'arabic-book',
        label: 'الكتاب الرسمي — عربي',
        shortLabel: 'الكتاب العربي',
        title: 'غلاف كتاب البرمجة والذكاء الاصطناعي أولى ثانوي — النسخة العربية',
        alt: 'غلاف كتاب البرمجة والذكاء الاصطناعي أولى ثانوي — النسخة العربية الرسمية',
        caption: 'غلاف الكتاب الرسمي عربي لمنهج البرمجة والذكاء الاصطناعي أولى ثانوي.',
        type: 'book',
        language: 'ar',
        src: null,
        width: null,
        height: null,
        pending: true,
        todo: 'Add Arabic book cover thumbnail when available.',
    },
    {
        id: 'english-book',
        label: 'Official English Book',
        shortLabel: 'English Book',
        title: 'Programming and Artificial Intelligence Grade 10 English book cover',
        alt: 'Official Programming and Artificial Intelligence Grade 10 English book cover',
        caption: 'Official English book cover for Grade 10 Programming and AI.',
        type: 'book',
        language: 'en',
        src: null,
        width: null,
        height: null,
        pending: true,
        todo: 'Add English book cover thumbnail when available.',
    },
];

function lesson(code, arabicTitle, englishTitle, status, type = 'theory') {
    return {
        code,
        arabicTitle,
        englishTitle,
        status,
        type,
        source: 'arabic-book',
    };
}

function summarizeStatuses(lessons) {
    const statuses = [...new Set(lessons.map((l) => l.status))];
    if (statuses.length === 1) {
        return STATUS_LABELS[statuses[0]];
    }
    const taught = statuses.filter((s) => s === 'term-1' || s === 'term-2');
    if (taught.length && statuses.includes('not-in-distribution')) {
        const taughtLabels = taught.map((s) => STATUS_LABELS[s]).join(' و');
        return `${taughtLabels}؛ ودروس أخرى موجودة في الكتاب وغير مدرجة في التوزيع الرسمي 2025/2026`;
    }
    return taught.map((s) => STATUS_LABELS[s]).join(' · ');
}

export const curriculumUnits = [
    {
        number: 1,
        id: 'unit-1-what-is-information',
        slug: 'unit-1-what-is-information',
        arabicTitle: 'ما هي المعلومات؟',
        englishTitle: 'What is Information?',
        icon: 'Info',
        summary:
            'يتعلم الطالب الفرق بين البيانات والمعلومات والمعرفة، ويفهم خصائص المعلومات مثل الاستمرارية، وقابلية النسخ، والانتشار. كما يتعرف على أنواع الوسائط، والثقافة الإعلامية، والمعلومات الأولية والثانوية، وأهمية التحقق من أكثر من مصدر. ويتناول الجزء الثاني أخلاقيات نشر المعلومات، والخصوصية، والمعلومات المضللة، والتنمر الإلكتروني، والعلامات الجغرافية، ومشكلات الاستخدام الخاطئ للهواتف ووسائل التواصل.',
        concepts: [
            'Data',
            'Information',
            'Knowledge',
            'Media',
            'Media Literacy',
            'Primary and Secondary Information',
            'Information Ethics',
            'Geotagging',
            'Disinformation',
            'Cyberbullying',
        ],
        studentOutcome:
            'يفهم الطالب كيف تتحول البيانات إلى معلومات، وكيف يتعامل مع المحتوى الرقمي بصورة واعية ومسؤولة.',
        realLife:
            'التحقق من خبر على وسائل التواصل قبل مشاركته، وفهم خصوصية الموقع الجغرافي في الصور.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('1-1', 'المعلومات والوسائط', 'Information and Media', 'term-1', 'theory'),
            lesson('1-2', 'أخلاقيات المعلومات', 'Information Ethics', 'term-1', 'theory'),
        ],
        taughtStatuses: STATUS_LABELS['term-1'],
    },
    {
        number: 2,
        id: 'unit-2-regulations-and-rights',
        slug: 'unit-2-regulations-and-rights',
        arabicTitle: 'القوانين والحقوق في مجتمع المعلومات',
        englishTitle: 'Regulations and Rights in the Information Society',
        icon: 'Scale',
        summary:
            'يتعرف الطالب على البيانات الشخصية والحساسة، والخصوصية، وحقوق الصورة، وسياسات حماية البيانات. كما يدرس حقوق الملكية الفكرية، وبراءات الاختراع، والعلامات التجارية، وحقوق المؤلف والحقوق المجاورة. ويتناول كيفية استخدام أعمال الآخرين بصورة قانونية، وقواعد الاقتباس، والاستخدام العادل، وتراخيص Creative Commons.',
        concepts: [
            'Personal Information',
            'Privacy',
            'Image Rights',
            'Intellectual Property',
            'Copyright',
            'Patents',
            'Trademarks',
            'Quotation',
            'Fair Use',
            'Creative Commons',
        ],
        studentOutcome: 'يتعلم الطالب كيف يحمي بياناته ويحترم حقوق أصحاب المحتوى.',
        realLife:
            'قراءة سياسة الخصوصية لتطبيق، واستخدام صور أو مقاطع بترخيص صحيح دون انتهاك حقوق المؤلف.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('2-1', 'البيانات الشخصية', 'Personal Information', 'term-1', 'theory'),
            lesson('2-2', 'حقوق الملكية الفكرية', 'Intellectual Property Rights', 'term-1', 'theory'),
            lesson(
                '2-3',
                'استخدام المعلومات والكشف عنها',
                'Use and Disclosure of Information',
                'term-1',
                'theory'
            ),
        ],
        taughtStatuses: STATUS_LABELS['term-1'],
    },
    {
        number: 3,
        id: 'unit-3-information-security',
        slug: 'unit-3-information-security',
        arabicTitle: 'أمن المعلومات',
        englishTitle: 'Information Security',
        icon: 'Shield',
        summary:
            'يتعلم الطالب عناصر أمن المعلومات الثلاثة: Confidentiality وIntegrity وAvailability. ويتعرّف على البرامج الضارة، والفيروسات، وTrojan Horse، وWorms، وSpyware، وRansomware. كما يدرس كلمات المرور، وأنواع المصادقة، وTwo-Factor Authentication، وFirewalls، وPhishing، وSocial Engineering. الكتاب الكامل يتضمن أيضًا التشفير بالمفتاح المتماثل والعام، وDigital Signatures وSSL/TLS.',
        concepts: [
            'Confidentiality',
            'Integrity',
            'Availability',
            'Malware',
            'Phishing',
            'Social Engineering',
            'Two-Factor Authentication',
            'Firewalls',
            'Encryption',
            'SSL/TLS',
            'Digital Signatures',
        ],
        studentOutcome:
            'يتعرف الطالب على تهديدات أمن المعلومات الأساسية وطرق الحماية اليومية للحسابات والبيانات.',
        realLife:
            'إنشاء كلمة مرور قوية، تفعيل التحقق بخطوتين، وتجنب روابط التصيد في الرسائل.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson(
                '3-1',
                'تهديدات وإجراءات مواجهة أمن المعلومات 1',
                'Threats and Countermeasures [1]',
                'term-1',
                'theory'
            ),
            lesson(
                '3-2',
                'تهديدات وإجراءات مواجهة أمن المعلومات 2',
                'Threats and Countermeasures [2]',
                'term-1',
                'theory'
            ),
            lesson(
                '3-3',
                'تهديدات وإجراءات مواجهة أمن المعلومات 3',
                'Threats and Countermeasures [3]',
                'term-1',
                'theory'
            ),
            lesson(
                '3-4',
                'تقنيات المعلومات للسلامة 1',
                'Information Technology for Safety [1]',
                'not-in-distribution',
                'theory'
            ),
            lesson(
                '3-5',
                'تقنيات المعلومات للسلامة 2',
                'Information Technology for Safety [2]',
                'not-in-distribution',
                'theory'
            ),
        ],
        taughtStatuses: '',
    },
    {
        number: 4,
        id: 'unit-4-it-and-society',
        slug: 'unit-4-it-and-society',
        arabicTitle: 'تكنولوجيا المعلومات والمجتمع',
        englishTitle: 'Information Technology and Society',
        icon: 'Users',
        summary:
            'يشرح الفصل تطور المجتمع من مجتمع الصيد والزراعة إلى المجتمع الصناعي ومجتمع المعلومات. ويتعرّف الطالب على Society 5.0 وBig Data وArtificial Intelligence وMachine Learning وIoT وSmart Homes وVR وAR وDigital Divide وTechnostress.',
        concepts: [
            'Society 5.0',
            'Big Data',
            'Artificial Intelligence',
            'Machine Learning',
            'IoT',
            'Smart Homes',
            'VR',
            'AR',
            'Digital Divide',
            'Technostress',
        ],
        studentOutcome: 'يرى الطالب كيف غيّرت التكنولوجيا المجتمع والحياة والعمل.',
        realLife:
            'ملاحظة تأثير الهواتف الذكية والمنازل الذكية والفجوة الرقمية على الحياة اليومية.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson(
                '4-1',
                'تطور تكنولوجيا المعلومات',
                'Development of Information Technology',
                'not-in-distribution',
                'theory'
            ),
        ],
        taughtStatuses: STATUS_LABELS['not-in-distribution'],
    },
    {
        number: 5,
        id: 'unit-5-communication',
        slug: 'unit-5-communication',
        arabicTitle: 'الاتصالات',
        englishTitle: 'Communication',
        icon: 'Radio',
        summary:
            'يتعرف الطالب على تطور وسائل الاتصال، والفرق بين أشكال الاتصال، وخصائص التواصل عبر الإنترنت. ويربط الفصل بين نقل المعلومات والشبكات والوسائط الحديثة.',
        concepts: [
            'Communication Media',
            'Forms of Communication',
            'Internet Communication',
            'Information Transfer',
            'Modern Media',
        ],
        studentOutcome:
            'يفهم الطالب كيف تطورت وسائل الاتصال وكيف تؤثر خصائص الإنترنت على نقل المعلومات.',
        realLife: 'المقارنة بين مكالمة هاتفية ورسالة نصية ومكالمة فيديو من حيث التوقيت والوسيط.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson(
                '5-1',
                'تطوير وسائل الاتصال',
                'Development of Communication Media',
                'not-in-distribution',
                'theory'
            ),
            lesson(
                '5-2',
                'الاتصال وأشكاله',
                'Communication and Its Forms',
                'not-in-distribution',
                'theory'
            ),
            lesson(
                '5-3',
                'الإنترنت والاتصالات',
                'Internet and Communication',
                'not-in-distribution',
                'theory'
            ),
        ],
        taughtStatuses: STATUS_LABELS['not-in-distribution'],
    },
    {
        number: 6,
        id: 'unit-6-information-design',
        slug: 'unit-6-information-design',
        arabicTitle: 'تصميم المعلومات',
        englishTitle: 'Information Design',
        icon: 'Binary',
        summary:
            'يتعرف الطالب على الطريقة التي يمثل بها الكمبيوتر البيانات باستخدام 0 و1. يدرس Analog and Digital وBits and Bytes وBinary وHexadecimal وCharacter Encoding والعمليات الحسابية الثنائية ورقمنة الصوت والصور وضغط الفيديو وتصميم المعلومات.',
        concepts: [
            'Analog and Digital',
            'Bits and Bytes',
            'Binary',
            'Hexadecimal',
            'Character Encoding',
            'Binary Arithmetic',
            'Sound Digitization',
            'Image Digitization',
            'Video Compression',
            'Information Design',
        ],
        studentOutcome:
            'يفهم الطالب كيف تتحول النصوص والصور والأرقام إلى بيانات يستطيع الكمبيوتر تخزينها ومعالجتها.',
        realLife:
            'فهم لماذا تشغل الصور عالية الجودة مساحة أكبر، وكيف تُخزَّن الألوان والنصوص رقميًا.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('6-1', 'التناظري والرقمي', 'Analog and Digital', 'term-1', 'theory'),
            lesson(
                '6-2',
                'النظام الثنائي وكمية البيانات',
                'Binary and Amount of Information',
                'term-1',
                'theory'
            ),
            lesson('6-3', 'النظام السادس عشر Hexadecimal', 'Hexadecimal', 'term-1', 'theory'),
            lesson(
                '6-4',
                'التمثيل الرقمي للأحرف',
                'Digital Representation of Characters',
                'term-1',
                'theory'
            ),
            lesson(
                '6-5',
                'العمليات الحسابية العددية 1',
                'Numerical Calculations [1]',
                'term-1',
                'theory'
            ),
            lesson(
                '6-6',
                'العمليات الحسابية العددية 2',
                'Numerical Calculations [2]',
                'term-1',
                'theory'
            ),
            lesson(
                '6-7',
                'رقمنة الصوت',
                'Digitalization of Sound',
                'not-in-distribution',
                'theory'
            ),
            lesson('6-8', 'رقمنة الصور', 'Digitization of Images', 'term-1', 'theory'),
            lesson(
                '6-9',
                'التمثيل الرقمي وضغط الفيديو',
                'Digital Representation and Compression of Video',
                'not-in-distribution',
                'theory'
            ),
            lesson('6-10', 'تصميم المعلومات', 'Information Design', 'term-1', 'theory'),
        ],
        taughtStatuses: '',
    },
    {
        number: 7,
        id: 'unit-7-computers',
        slug: 'unit-7-computers',
        arabicTitle: 'الكمبيوترات',
        englishTitle: 'Computers',
        icon: 'Monitor',
        summary:
            'يتعرف الطالب على المكونات الرئيسية للكمبيوتر، والفرق بين Hardware وSoftware، ودور نظام التشغيل وبرامج التشغيل. كما يحتوي الكتاب على Logic Circuits، والبوابات المنطقية، ودائرة الجمع النصفي والكامل.',
        concepts: [
            'Hardware',
            'Software',
            'Operating System',
            'Computer Configuration',
            'Logic Circuits',
            'Logic Gates',
            'Half Adder',
            'Full Adder',
        ],
        studentOutcome:
            'يفهم الطالب بنية الحاسب والفرق بين المكونات المادية والبرمجيات، ويعرف وجود الدوائر المنطقية في الكتاب.',
        realLife: 'معرفة دور المعالج والذاكرة ونظام التشغيل عند تشغيل جهاز أو تطبيق.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('7-1', 'بنية الكمبيوتر', 'Computer Configuration', 'term-1', 'theory'),
            lesson('7-2', 'برامج الكمبيوتر', 'Computer Software', 'term-1', 'theory'),
            lesson('7-3', 'الدوائر المنطقية', 'Logic Circuits', 'not-in-distribution', 'theory'),
        ],
        taughtStatuses: '',
    },
    {
        number: 8,
        id: 'unit-8-networks',
        slug: 'unit-8-networks',
        arabicTitle: 'الشبكات',
        englishTitle: 'Networks',
        icon: 'Network',
        summary:
            'يتعرف الطالب على كيفية اتصال الأجهزة، وأنواع الشبكات، وعناوين IP، وأسماء النطاقات، والبروتوكولات. ويوضح الكتاب كيف تعمل صفحات الويب والبريد الإلكتروني، وكيف تُحسب سرعة نقل البيانات.',
        concepts: [
            'Computer Networks',
            'IP Addresses',
            'Domain Names',
            'Communication Protocols',
            'Web Pages',
            'Email',
            'Network Transfer Speed',
        ],
        studentOutcome:
            'يفهم الطالب أساسيات الشبكات وعناوين IP وأسماء النطاقات وبروتوكولات الاتصال.',
        realLife: 'فهم معنى عنوان IP واسم النطاق عند فتح موقع أو إرسال بريد إلكتروني.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('8-1', 'شبكات الكمبيوتر', 'Computer Networks', 'term-2', 'theory'),
            lesson(
                '8-2',
                'عناوين IP وأسماء النطاقات',
                'IP Addresses and Domain Names',
                'term-2',
                'theory'
            ),
            lesson('8-3', 'بروتوكولات الاتصال', 'Communication Protocol', 'term-2', 'theory'),
            lesson(
                '8-4',
                'آلية عمل صفحات الويب والبريد الإلكتروني',
                'Mechanism of Web Pages and Emails',
                'not-in-distribution',
                'theory'
            ),
            lesson(
                '8-5',
                'سرعة نقل البيانات على الشبكات',
                'Network Transfer Speed',
                'not-in-distribution',
                'theory'
            ),
        ],
        taughtStatuses: '',
    },
    {
        number: 9,
        id: 'unit-9-databases',
        slug: 'unit-9-databases',
        arabicTitle: 'قواعد البيانات',
        englishTitle: 'Databases',
        icon: 'Database',
        summary:
            'يتعرف الطالب على قواعد البيانات ونظام إدارة قواعد البيانات، وأنواع قواعد البيانات، والجداول والعلاقات. كما يتناول الكتاب نظم المعلومات، وتكامل النظم، وأنظمة ERP.',
        concepts: [
            'Database',
            'DBMS',
            'Tables',
            'Relationships',
            'Information Systems',
            'System Integration',
            'ERP',
        ],
        studentOutcome: 'يفهم الطالب فكرة قواعد البيانات والجداول والعلاقات بين البيانات.',
        realLife: 'تصور بيانات الطلاب في جداول مرتبطة داخل منصة تعليمية أو نظام مدرسة.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('9-1', 'قواعد البيانات 1', 'Database [1]', 'term-2', 'theory'),
            lesson('9-2', 'قواعد البيانات 2', 'Database [2]', 'term-2', 'theory'),
            lesson(
                '9-3',
                'نظم المعلومات المختلفة',
                'Various Information Systems',
                'not-in-distribution',
                'theory'
            ),
        ],
        taughtStatuses: '',
    },
    {
        number: 10,
        id: 'unit-10-data-analysis',
        slug: 'unit-10-data-analysis',
        arabicTitle: 'تحليل البيانات',
        englishTitle: 'Data Analysis',
        icon: 'BarChart3',
        summary:
            'يتعلم الطالب كيف يجمع البيانات، ويصفها، ويقارنها، ويكتشف العلاقات والاتجاهات الموجودة داخلها. كما يتعرف على أدوات إحصائية ورسوم تساعد في تفسير النتائج واتخاذ القرارات.',
        concepts: [
            'Data Types',
            'Data Collection',
            'Quartiles',
            'Box-and-Whisker Plots',
            'Variance',
            'Standard Deviation',
            'Scatter Plots',
            'Correlations',
            'Cross Tabulation',
            'Regression Analysis',
        ],
        studentOutcome:
            'يتعرف الطالب على طرق وصف البيانات واكتشاف العلاقات بينها باستخدام أدوات التحليل الأساسية.',
        realLife: 'قراءة نتائج استبيان أو درجات فصل باستخدام الرسوم والمقاييس الإحصائية البسيطة.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson(
                '10-1',
                'أنواع البيانات والتحليل',
                'Types of Data and Analysis',
                'not-in-distribution',
                'theory'
            ),
            lesson('10-2', 'جمع البيانات', 'Data Collection', 'not-in-distribution', 'theory'),
            lesson('10-3', 'الربيعيات Quartiles', 'Quartiles', 'not-in-distribution', 'theory'),
            lesson(
                '10-4',
                'مخطط الصندوق والطرفين',
                'Box-and-Whisker Plots',
                'not-in-distribution',
                'theory'
            ),
            lesson('10-5', 'التباين Variance', 'Variance', 'not-in-distribution', 'theory'),
            lesson(
                '10-6',
                'الانحراف المعياري',
                'Standard Deviation',
                'not-in-distribution',
                'theory'
            ),
            lesson(
                '10-7',
                'مخططات الانتشار',
                'Scatter Plots',
                'not-in-distribution',
                'theory'
            ),
            lesson('10-8', 'الارتباطات', 'Correlations', 'not-in-distribution', 'theory'),
            lesson(
                '10-9',
                'الجداول المتقاطعة',
                'Cross Tabulation',
                'not-in-distribution',
                'theory'
            ),
            lesson(
                '10-10',
                'تحليل الانحدار',
                'Regression Analysis',
                'not-in-distribution',
                'theory'
            ),
        ],
        taughtStatuses: STATUS_LABELS['not-in-distribution'],
    },
    {
        number: 11,
        id: 'unit-11-simulations',
        slug: 'unit-11-simulations',
        arabicTitle: 'المحاكاة',
        englishTitle: 'Simulations',
        icon: 'FlaskConical',
        summary:
            'يتعلم الطالب كيف نبني نموذجًا مبسطًا لنظام حقيقي ونستخدمه لتجربة سيناريوهات مختلفة. يتناول الفصل جداول البيانات، والمراجع، والوظائف، والنماذج الاحتمالية، وقوائم الانتظار.',
        concepts: [
            'Modeling',
            'Simulation',
            'Spreadsheets',
            'References',
            'Functions',
            'Probabilistic Models',
            'Queues',
        ],
        studentOutcome:
            'يفهم الطالب فكرة النمذجة والمحاكاة واستخدام الجداول لتجربة سيناريوهات مختلفة.',
        realLife: 'محاكاة طابور خدمة أو تجربة سيناريوهات بسيطة في جدول بيانات قبل التنفيذ الحقيقي.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('11-1', 'النمذجة', 'Modeling', 'not-in-distribution', 'theory'),
            lesson('11-2', 'المحاكاة 1', 'Simulation [1]', 'not-in-distribution', 'theory'),
            lesson('11-3', 'المحاكاة 2', 'Simulation [2]', 'not-in-distribution', 'theory'),
            lesson('11-4', 'قوائم الانتظار Queues', 'Queues', 'not-in-distribution', 'theory'),
        ],
        taughtStatuses: STATUS_LABELS['not-in-distribution'],
    },
    {
        number: 12,
        id: 'unit-12-programming',
        slug: 'unit-12-programming',
        arabicTitle: 'البرمجة',
        englishTitle: 'Programming',
        icon: 'Code2',
        summary:
            'يتعرف الطالب على معنى Algorithm وFlowcharts وهياكل التحكم. كما يتعلم Variables وSequential Structure وBranching وLoops وError Handling وData Structures. النسخة الإنجليزية تعرض هذا الفصل بمفاهيم Programming with Python.',
        concepts: [
            'Algorithm',
            'Flowcharts',
            'Variables',
            'Sequential Structure',
            'Branching',
            'Loops',
            'Error Handling',
            'Data Structures',
        ],
        studentOutcome:
            'يبدأ الطالب التفكير البرمجي من خلال الخوارزميات وهياكل التحكم الأساسية.',
        realLife: 'كتابة خطوات مرتبة لحل مشكلة يومية قبل تحويلها إلى كود أو مخطط تدفق.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson('12-1', 'الخوارزميات', 'Algorithm', 'term-2', 'theory'),
            lesson(
                '12-2',
                'أساسيات البرمجة 1',
                'Programming Basics [1]',
                'term-2',
                'practical'
            ),
            lesson(
                '12-3',
                'أساسيات البرمجة 2',
                'Programming Basics [2]',
                'term-2',
                'practical'
            ),
            lesson(
                '12-4',
                'البرمجة التطبيقية 1',
                'Application of Programming [1]',
                'not-in-distribution',
                'practical'
            ),
            lesson(
                '12-5',
                'البرمجة التطبيقية 2',
                'Application of Programming [2]',
                'not-in-distribution',
                'practical'
            ),
        ],
        taughtStatuses: '',
    },
    {
        number: 13,
        id: 'unit-13-ai-programming',
        slug: 'unit-13-ai-programming',
        arabicTitle: 'الذكاء الاصطناعي التوليدي ومشروعات الويب',
        englishTitle: 'AI Programming',
        icon: 'Brain',
        summary:
            'ينتقل الطالب هنا من دراسة المفاهيم إلى بناء مشروعات. يتعرف على الذكاء الاصطناعي التوليدي، ثم يستخدم HTML وCSS وJavaScript لإنشاء صفحات ثابتة وتفاعلية. بعد ذلك يطور تطبيق اختبارات ولعبة Block Breaker، ويعرض مشروعه ويشرح مراحل بنائه.',
        concepts: [
            'Generative AI',
            'HTML',
            'CSS',
            'JavaScript',
            'Static Web Pages',
            'Dynamic Web Pages',
            'Quiz App',
            'Block Breaker',
            'Project Presentation',
        ],
        studentOutcome:
            'ينهي الطالب العام بفهم مبدئي لطريقة بناء مشروع رقمي بدل الاكتفاء باستخدام التكنولوجيا.',
        realLife:
            'بناء صفحة ويب بسيطة أو تطبيق اختبارات، واستخدام أدوات AI كمساعد أثناء التطوير بعقل واعٍ.',
        bookAvailability: 'ar+en',
        lessons: [
            lesson(
                '13-1',
                'مقدمة الذكاء الاصطناعي التوليدي',
                'About Generative AI',
                'term-2',
                'theory'
            ),
            lesson('13-2', 'HTML وCSS', 'HTML and CSS', 'term-2', 'practical'),
            lesson('13-3', 'HTML وJavaScript', 'HTML and JavaScript', 'term-2', 'practical'),
            lesson(
                '13-4',
                'إنشاء صفحات ويب ثابتة',
                'Creating Static Web Pages',
                'term-2',
                'project'
            ),
            lesson(
                '13-5',
                'إنشاء صفحات ويب تفاعلية',
                'Creating Dynamic Web Pages',
                'term-2',
                'project'
            ),
            lesson(
                '13-6',
                'عرض موقع الويب',
                'Presenting Your Website',
                'term-2',
                'project'
            ),
            lesson(
                '13-7',
                'تطوير تطبيق اختبارات',
                'Developing a Quiz App',
                'term-2',
                'project'
            ),
            lesson('13-8', 'مراجعة تطبيق الاختبارات', 'Quiz App Review', 'term-2', 'project'),
            lesson(
                '13-9',
                'تطوير لعبة تكسير القوالب 1',
                'Developing a Block Breaker Game 1',
                'term-2',
                'project'
            ),
            lesson(
                '13-10',
                'تطوير لعبة تكسير القوالب 2',
                'Developing a Block Breaker Game 2',
                'term-2',
                'project'
            ),
            lesson(
                '13-11',
                'ملخص لعبة تكسير القوالب',
                'Block Breaker Summary',
                'term-2',
                'project'
            ),
            lesson(
                '13-12',
                'عرض مشروع لعبة تكسير القوالب',
                'Block Breaker Development Presentation',
                'term-2',
                'project'
            ),
        ],
        taughtStatuses: STATUS_LABELS['term-2'],
    },
];

// Fill mixed-unit taughtStatuses from lesson status sets.
curriculumUnits.forEach((unit) => {
    if (!unit.taughtStatuses) {
        unit.taughtStatuses = summarizeStatuses(unit.lessons);
    }
});

export const termOneWeeks = [
    {
        week: 1,
        label: 'الأسبوع 1',
        bookLessons: [
            { code: '1-1', title: 'المعلومات والوسائط' },
            { code: '1-2', title: 'أخلاقيات المعلومات' },
            { code: '2-1', title: 'البيانات الشخصية' },
        ],
        platformItems: ['التعرف على المنصة ومستوياتها'],
        qureoItems: [],
        notes: '',
    },
    {
        week: 2,
        label: 'الأسبوع 2',
        bookLessons: [
            { code: '2-2', title: 'حقوق الملكية الفكرية' },
            { code: '2-3', title: 'استخدام المعلومات والكشف عنها' },
            { code: '3-1', title: 'تهديدات وإجراءات مواجهة أمن المعلومات 1' },
        ],
        platformItems: [],
        qureoItems: [],
        notes: '',
    },
    {
        week: 3,
        label: 'الأسبوع 3',
        bookLessons: [
            { code: '3-2', title: 'تهديدات وإجراءات مواجهة أمن المعلومات 2' },
            { code: '3-3', title: 'تهديدات وإجراءات مواجهة أمن المعلومات 3' },
            { code: '6-1', title: 'التناظري والرقمي' },
        ],
        platformItems: ['الاستعداد لاختبار المستوى'],
        qureoItems: [],
        notes: '',
    },
    {
        week: 4,
        label: 'الأسبوع 4',
        bookLessons: [
            { code: '6-2', title: 'النظام الثنائي وكمية البيانات' },
            { code: '6-3', title: 'Hexadecimal' },
        ],
        platformItems: [],
        qureoItems: ['Introduction', 'Calculations and Strings'],
        notes: '',
    },
    {
        week: 5,
        label: 'الأسبوع 5',
        bookLessons: [
            { code: '6-4', title: 'التمثيل الرقمي للأحرف' },
            { code: '6-5', title: 'العمليات الحسابية العددية 1' },
        ],
        platformItems: [],
        qureoItems: ['Variables', 'Comprehensive Check'],
        notes: '',
    },
    {
        week: 6,
        label: 'الأسبوع 6',
        bookLessons: [{ code: '6-6', title: 'العمليات الحسابية العددية 2' }],
        platformItems: [],
        qureoItems: ['If Statement', 'If-Else Statements'],
        notes: '',
    },
    {
        week: 7,
        label: 'الأسبوع 7',
        bookLessons: [
            { code: '6-8', title: 'رقمنة الصور' },
            { code: '6-10', title: 'تصميم المعلومات' },
        ],
        platformItems: [],
        qureoItems: ['Else-If', 'Comprehensive Check'],
        notes: '',
    },
    {
        week: 8,
        label: 'الأسبوع 8',
        bookLessons: [{ code: '7-1', title: 'بنية الكمبيوتر' }],
        platformItems: [],
        qureoItems: ['TOFAS trial test', 'Logical Operators', 'Interactive Operations'],
        notes: 'جانب عملي / اختبار تجريبي TOFAS مع عمليات منطقية وتفاعلية.',
    },
    {
        week: 9,
        label: 'الأسبوع 9',
        bookLessons: [{ code: '7-2', title: 'برامج الكمبيوتر' }],
        platformItems: [],
        qureoItems: ['Comprehensive Check'],
        notes: '',
    },
    {
        week: 'final',
        label: 'نهاية الترم الأول',
        bookLessons: [],
        platformItems: [],
        qureoItems: [],
        notes: 'مراجعة الكتاب · مراجعة شاملة على Qureo · الاستعداد لاختبار الترم الأول.',
        isFinalPeriod: true,
    },
];

export const termOneSummary =
    'الترم الأول جمع بين أساسيات المعلومات والأمان الرقمي، وتمثيل البيانات داخل الكمبيوتر، ومكونات الحاسب، وبداية JavaScript العملية على Qureo.';

export const termTwoWeeks = [
    {
        week: 1,
        label: 'الأسبوع 1',
        bookLessons: [
            { code: '8-1', title: 'شبكات الكمبيوتر' },
            { code: '8-2', title: 'عناوين IP وأسماء النطاقات' },
        ],
        platformItems: [],
        qureoItems: ['HTML [1]', 'CSS [1]'],
        notes: '',
    },
    {
        week: 2,
        label: 'الأسبوع 2',
        bookLessons: [
            { code: '8-3', title: 'بروتوكولات الاتصال' },
            { code: '9-1', title: 'قواعد البيانات 1' },
        ],
        platformItems: [],
        qureoItems: ['Comprehensive Check', 'Conditions'],
        notes: '',
    },
    {
        week: 3,
        label: 'الأسبوع 3',
        bookLessons: [{ code: '9-2', title: 'قواعد البيانات 2' }],
        platformItems: [],
        qureoItems: ['Looping [1]', 'Functions'],
        notes: '',
    },
    {
        week: 4,
        label: 'الأسبوع 4',
        bookLessons: [{ code: '12-1', title: 'الخوارزميات' }],
        platformItems: [],
        qureoItems: ['Comprehensive Check', 'Looping [2]'],
        notes: '',
    },
    {
        week: 5,
        label: 'الأسبوع 5',
        bookLessons: [{ code: '12-2', title: 'أساسيات البرمجة 1' }],
        platformItems: [],
        qureoItems: ['Array', 'Comprehensive Check'],
        notes: '',
    },
    {
        week: 6,
        label: 'الأسبوع 6',
        bookLessons: [
            { code: '12-3', title: 'أساسيات البرمجة 2' },
            { code: '13-1', title: 'مقدمة الذكاء الاصطناعي التوليدي' },
            { code: '13-2', title: 'HTML وCSS' },
        ],
        platformItems: [],
        qureoItems: ['For Of'],
        notes: '',
    },
    {
        week: 7,
        label: 'الأسبوع 7',
        bookLessons: [
            { code: '13-3', title: 'HTML وJavaScript' },
            { code: null, title: 'الصفحات التفاعلية' },
        ],
        platformItems: [],
        qureoItems: ['Comprehensive Check'],
        notes: '',
    },
    {
        week: 8,
        label: 'الأسبوع 8',
        bookLessons: [
            { code: '13-4', title: 'إنشاء صفحات ويب ثابتة' },
            { code: '13-5', title: 'إنشاء صفحات ويب تفاعلية' },
        ],
        platformItems: [],
        qureoItems: ['Comprehensive Check'],
        notes: 'استخدام Generative AI ضمن سير عمل مشروع الويب.',
    },
    {
        week: 9,
        label: 'الأسبوع 9',
        bookLessons: [
            { code: '13-6', title: 'عرض موقع الويب' },
            { code: '13-7', title: 'تطوير تطبيق اختبارات' },
        ],
        platformItems: ['مراجعة المنصة'],
        qureoItems: [],
        notes: '',
    },
    {
        week: 10,
        label: 'الأسبوع 10',
        bookLessons: [
            { code: '13-8', title: 'مراجعة تطبيق الاختبارات' },
            { code: '13-9', title: 'تطوير لعبة تكسير القوالب 1' },
        ],
        platformItems: ['مراجعة المنصة'],
        qureoItems: [],
        notes: '',
    },
    {
        week: 11,
        label: 'الأسبوع 11',
        bookLessons: [
            { code: '13-10', title: 'تطوير لعبة تكسير القوالب 2' },
            { code: '13-11', title: 'ملخص لعبة تكسير القوالب' },
        ],
        platformItems: ['مراجعة المنصة'],
        qureoItems: [],
        notes: '',
    },
    {
        week: 12,
        label: 'الأسبوع 12',
        bookLessons: [{ code: '13-12', title: 'عرض مشروع لعبة تكسير القوالب' }],
        platformItems: ['مراجعة المنصة'],
        qureoItems: [],
        notes: '',
    },
    {
        week: 'final',
        label: 'نهاية الترم الثاني',
        bookLessons: [],
        platformItems: [],
        qureoItems: [],
        notes: 'مراجعة عامة · الاستعداد لاختبار الترم الثاني.',
        isFinalPeriod: true,
    },
];

export const termTwoSummary =
    'الترم الثاني ركز على الشبكات وقواعد البيانات والخوارزميات، ثم انتقل إلى HTML وCSS وJavaScript والذكاء الاصطناعي التوليدي وبناء مشروعات ويب عملية.';

export const notScheduledByTerm = {
    term1: [
        {
            code: '3-4',
            arabicTitle: 'تقنيات المعلومات للسلامة 1',
            englishTitle: 'Information Technology for Safety [1]',
            unitNumber: 3,
        },
        {
            code: '3-5',
            arabicTitle: 'تقنيات المعلومات للسلامة 2',
            englishTitle: 'Information Technology for Safety [2]',
            unitNumber: 3,
        },
        {
            code: 'unit-4',
            arabicTitle: 'الوحدة 4: تكنولوجيا المعلومات والمجتمع',
            englishTitle: 'Unit 4: Information Technology and Society',
            unitNumber: 4,
            isWholeUnit: true,
        },
        {
            code: 'unit-5',
            arabicTitle: 'الوحدة 5: الاتصالات',
            englishTitle: 'Unit 5: Communication',
            unitNumber: 5,
            isWholeUnit: true,
        },
        {
            code: '6-7',
            arabicTitle: 'رقمنة الصوت',
            englishTitle: 'Digitalization of Sound',
            unitNumber: 6,
        },
        {
            code: '6-9',
            arabicTitle: 'التمثيل الرقمي وضغط الفيديو',
            englishTitle: 'Digital Representation and Compression of Video',
            unitNumber: 6,
        },
        {
            code: '7-3',
            arabicTitle: 'الدوائر المنطقية',
            englishTitle: 'Logic Circuits',
            unitNumber: 7,
        },
    ],
    term2: [
        {
            code: '8-4',
            arabicTitle: 'آلية عمل صفحات الويب والبريد الإلكتروني',
            englishTitle: 'Mechanism of Web Pages and Emails',
            unitNumber: 8,
        },
        {
            code: '8-5',
            arabicTitle: 'سرعة نقل البيانات على الشبكات',
            englishTitle: 'Network Transfer Speed',
            unitNumber: 8,
        },
        {
            code: '9-3',
            arabicTitle: 'نظم المعلومات المختلفة',
            englishTitle: 'Various Information Systems',
            unitNumber: 9,
        },
        {
            code: 'unit-10',
            arabicTitle: 'الوحدة 10: تحليل البيانات',
            englishTitle: 'Unit 10: Data Analysis',
            unitNumber: 10,
            isWholeUnit: true,
        },
        {
            code: 'unit-11',
            arabicTitle: 'الوحدة 11: المحاكاة',
            englishTitle: 'Unit 11: Simulations',
            unitNumber: 11,
            isWholeUnit: true,
        },
        {
            code: '12-4',
            arabicTitle: 'البرمجة التطبيقية 1',
            englishTitle: 'Application of Programming [1]',
            unitNumber: 12,
        },
        {
            code: '12-5',
            arabicTitle: 'البرمجة التطبيقية 2',
            englishTitle: 'Application of Programming [2]',
            unitNumber: 12,
        },
    ],
    warning:
        'هذه القائمة مبنية على مقارنة الكتاب الكامل بالتوزيع الرسمي المتاح 2025/2026. لا تحذف محتوى من نسخ الكتاب المعروضة، ولا تدّعِ أن الدرس حُذف نهائيًا من أي عام دراسي آخر.',
};

export const curriculumFaqItems = [
    {
        q: 'ما هو منهج البرمجة أولى ثانوي؟',
        a: 'هو منهج يجمع بين تكنولوجيا المعلومات، والأمان الرقمي، وتمثيل البيانات، والشبكات، وقواعد البيانات، والخوارزميات، والبرمجة، والذكاء الاصطناعي التوليدي ومشروعات الويب.',
    },
    {
        q: 'كم وحدة في كتاب البرمجة أولى ثانوي؟',
        a: 'يحتوي الكتاب الكامل المتاح للعام الدراسي 2025/2026 على 13 وحدة تبدأ بالمعلومات وتنتهي بمشروعات الذكاء الاصطناعي والويب.',
    },
    {
        q: 'هل يوجد كتاب عربي وكتاب لغات؟',
        a: 'نعم. تتوفر نسخة عربية ونسخة English لطلاب مدارس اللغات، ويمكن تحميل النسختين من الصفحة.',
    },
    {
        q: 'هل تحميل الكتاب مجاني؟',
        a: 'نعم، يمكن تحميل ملفات الكتاب المتاحة من روابط التحميل الموجودة في الصفحة.',
    },
    {
        q: 'هل كل الدروس الموجودة في الكتاب مقررة؟',
        a: 'لا بالضرورة. الكتاب يحتوي على المحتوى الكامل، بينما يحدد التوزيع الرسمي الدروس التي يتم تدريسها خلال كل ترم.',
    },
    {
        q: 'ما المحذوف من منهج البرمجة أولى ثانوي؟',
        a: 'نستخدم تعبير “غير مدرج في التوزيع الرسمي” بدلًا من “محذوف من الكتاب”. تعرض الصفحة قائمة الدروس الموجودة داخل الكتاب والتي لم تظهر في توزيع 2025/2026.',
    },
    {
        q: 'ماذا درس الطلاب في الترم الأول؟',
        a: 'شمل الترم الأول المعلومات والأخلاقيات والحقوق، وأساسيات أمن المعلومات، والأنظمة الثنائية وHexadecimal، وتمثيل البيانات، والصور، ومكونات الكمبيوتر، إلى جانب أساسيات JavaScript على Qureo.',
    },
    {
        q: 'ماذا درس الطلاب في الترم الثاني؟',
        a: 'شمل الترم الثاني الشبكات، وعناوين IP، والبروتوكولات، وقواعد البيانات، والخوارزميات، وأساسيات البرمجة، وHTML وCSS وJavaScript، والذكاء الاصطناعي التوليدي ومشروعات الويب.',
    },
    {
        q: 'هل المنهج نظري أم عملي؟',
        a: 'يجمع المنهج بين الكتاب النظري، والتطبيق العملي على Qureo JavaScript، ومشروعات HTML وCSS وJavaScript والذكاء الاصطناعي التوليدي.',
    },
    {
        q: 'هل يحتاج الطالب إلى لابتوب؟',
        a: 'يمكن دراسة الجزء النظري من الكتاب باستخدام أي جهاز مناسب، لكن وجود كمبيوتر أو لابتوب يساعد الطالب بصورة أكبر في كتابة الكود وتنفيذ المشروعات العملية.',
    },
    {
        q: 'هل منهج العربي مختلف عن اللغات؟',
        a: 'لغة الشرح والمصطلحات تختلف، لكن النسختين تغطيان البناء العام نفسه للمفاهيم والوحدات والمشروعات.',
    },
    {
        q: 'هل وحدات تحليل البيانات والمحاكاة موجودة في الكتاب؟',
        a: 'نعم، هي موجودة في الكتاب الكامل، لكنها لم تظهر ضمن التوزيع الرسمي المتاح للعام 2025/2026.',
    },
    {
        q: 'هل Generative AI جزء من الكتاب؟',
        a: 'نعم. الوحدة الثالثة عشرة تتناول الذكاء الاصطناعي التوليدي وتربطه ببناء صفحات ويب وتطبيق اختبارات ولعبة بسيطة.',
    },
    {
        q: 'كيف أشترك في كورس أولى ثانوي؟',
        a: 'استخدم زر “ابدأ كورس أولى ثانوي” للانتقال إلى صفحة الكورس ومعرفة تفاصيل الاشتراك والتفعيل.',
    },
    {
        q: 'هل سيتم تحديث الصفحة للعام 2026/2027؟',
        a: 'نعم. سيتم تحديث الكتاب والتوزيع والدروس فور توفر المصادر الرسمية الجديدة.',
    },
];

/** Units that include at least one lesson matching the given status. */
export function filterUnitsByStatus(status) {
    if (!status || status === 'all') return curriculumUnits;
    return curriculumUnits.filter((unit) => unit.lessons.some((l) => l.status === status));
}

/** Units that include at least one lesson matching the given type. */
export function filterUnitsByType(type) {
    if (!type || type === 'all') return curriculumUnits;
    return curriculumUnits.filter((unit) => unit.lessons.some((l) => l.type === type));
}

/** Flat list of lessons with a given status across all units. */
export function getLessonsByStatus(status) {
    return curriculumUnits.flatMap((unit) =>
        unit.lessons
            .filter((l) => l.status === status)
            .map((l) => ({
                ...l,
                unitNumber: unit.number,
                unitArabicTitle: unit.arabicTitle,
                unitId: unit.id,
            }))
    );
}

export function getStatusLabel(status) {
    return STATUS_LABELS[status] || status;
}

export function getUnitBySlug(slug) {
    return curriculumUnits.find((u) => u.slug === slug || u.id === slug) || null;
}

export function countLessonsByStatus() {
    const counts = { 'term-1': 0, 'term-2': 0, 'not-in-distribution': 0 };
    curriculumUnits.forEach((unit) => {
        unit.lessons.forEach((l) => {
            if (counts[l.status] !== undefined) counts[l.status] += 1;
        });
    });
    return counts;
}
